// Which product modules the organisation's plan includes.
//
// Roles say what a person may open; the plan says what the organisation has
// bought. Both gate the same five sellable modules (cases, AML, flows, forms,
// proof of address), and the RBAC module name (`case:viewer` -> "case") is the
// key for both, so the sidebar lock and the route guard can ask one question of
// each. The flags come from GET /billing/module-availability (open to every
// member, carries no prices) and live in the auth store for the session.
//
// Unknown is open: a module the map says nothing about, or an empty map because
// the request failed, never locks anything. Mis-locking a paid-for feature is a
// worse failure than showing a sellable one the plan lacks, and the backend
// refuses to bill an unavailable metric either way.
import { moduleOf, PLATFORM_MODULE } from './rbac';

export type ModuleAvailability = Record<string, boolean>;

export const SELLABLE_MODULES = ['case', 'aml', 'cfm', 'forms', 'poa'] as const;

const isSellableModule = (module: string): boolean =>
	(SELLABLE_MODULES as readonly string[]).includes(module);

export const isModuleAvailable = (
	availability: ModuleAvailability | null | undefined,
	module: string
): boolean => availability?.[module] !== false;

// The module a sidebar lock claim or a route's allowedClaims belongs to, or
// undefined when the claims are not about one sellable module: 'All', system
// claims and platform roles gate on who the person is, not on what the plan
// includes, and a page any of several modules can open is still open while one
// of them is in the plan.
export const sellableModuleOf = (claim: string): string | undefined => {
	const module = moduleOf(claim);
	return isSellableModule(module) && module !== PLATFORM_MODULE
		? module
		: undefined;
};

// A route is outside the plan only when every claim that could open it is a
// role in a module the plan lacks. Any other way in (a platform role, 'All', a
// module that is included, no claims at all) keeps it open; the role check that
// follows still applies.
export const isRouteInPlan = (
	allowedClaims: readonly string[] | undefined,
	availability: ModuleAvailability | null | undefined
): boolean => {
	if (!allowedClaims?.length) {
		return true;
	}
	const modules = allowedClaims.map(sellableModuleOf);
	if (modules.some((module) => module === undefined)) {
		return true;
	}
	return modules.some((module) =>
		isModuleAvailable(availability, module as string)
	);
};

// Parses the endpoint's `modules` object, keeping only boolean flags so a
// malformed payload degrades to "unknown" rather than to a lock.
export const parseModuleAvailability = (raw: unknown): ModuleAvailability => {
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
		return {};
	}
	return Object.fromEntries(
		Object.entries(raw as Record<string, unknown>).filter(
			(entry): entry is [string, boolean] => typeof entry[1] === 'boolean'
		)
	);
};
